import { getContentConfig } from "../api/requests/player.requests.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { isConfigMissing } from "../utils/network_errors.js";
import type { DailyTaskRewardDto } from "../api/dto/daily_tasks.dto.js";

const CONVEYOR_CONFIG_CACHE_KEY = "conveyor_config_v1";

export interface ConveyorLevel
{
  seconds: number;
  retry_price: number;
  speed_multiplier: number;
  rewards: DailyTaskRewardDto[];
}

export interface ConveyorConfig
{
  levels: ConveyorLevel[];
  start_speed: number;
  max_speed: number;
  ramp_sec: number;
  fill_sec: number;
  wave_min: number;
  wave_max: number;
  breath_sec: number;
  block_gap_sec: number;
  freeze_sec: number;
  structure_max_depth: number;
  structure_max_cols: number;
  structure_max_rows: number;
  light_max_depth: number;
  light_max_cols: number;
  light_max_rows: number;
  heavy_first_chance: number;
  heavy_after_light_chance: number;
  heavy_after_heavy_chance: number;
}

const DEFAULT_LEVEL_SECONDS = [ 15, 20, 25, 30, 35, 40, 45, 50, 55, 60 ];

const DEFAULT_CONFIG: ConveyorConfig = {
  levels: DEFAULT_LEVEL_SECONDS.map( ( seconds, i ) => ( {
    seconds,
    retry_price: 600,
    speed_multiplier: 1,
    rewards: [ { type: "coin", amount: 200 + i * 50 } ],
  } ) ),
  start_speed: 0.35,
  max_speed: 0.9,
  ramp_sec: 90,
  fill_sec: 2,
  wave_min: 3,
  wave_max: 5,
  breath_sec: 2.5,
  block_gap_sec: 0,
  freeze_sec: 5,
  structure_max_depth: 3,
  structure_max_cols: 8,
  structure_max_rows: 8,
  light_max_depth: 2,
  light_max_cols: 6,
  light_max_rows: 6,
  heavy_first_chance: 0.5,
  heavy_after_light_chance: 0.5,
  heavy_after_heavy_chance: 0,
};

const MAX_SPEED = 5;
const MAX_SPEED_MULTIPLIER = 10;
const MAX_SECONDS = 3600;
const MAX_FILL_SEC = 60;
const MAX_WAVE = 100;
const MAX_BREATH_SEC = 60;
const MAX_STRUCTURE_SIZE = 30;

function positiveNumber( value: unknown, fallback: number, max: number ): number
{
  const number = typeof value === "number" ? value : Number( value );
  return Number.isFinite( number ) && number > 0 && number <= max ? number : fallback;
}

function isRecord( value: unknown ): value is Record<string, unknown>
{
  return !!value && typeof value === "object" && !Array.isArray( value );
}

function isCount( value: unknown, min: number ): value is number
{
  return typeof value === "number" && Number.isInteger( value ) && value >= min;
}

function parseLevel( raw: unknown ): ConveyorLevel | null
{
  if ( !isRecord( raw ) || !isCount( raw.seconds, 1 ) || !isCount( raw.retry_price, 0 ) || !Array.isArray( raw.rewards ) )
    return null;

  const rewards = raw.rewards.filter( ( r ): r is DailyTaskRewardDto => isRecord( r ) && typeof r.type === "string" && isCount( r.amount, 1 ) );

  const speed_multiplier = positiveNumber( raw.speed_multiplier, 1, MAX_SPEED_MULTIPLIER );

  return { seconds: raw.seconds, retry_price: raw.retry_price, speed_multiplier, rewards };
}

function parseLevels( raw: unknown ): ConveyorLevel[]
{
  const levels = Array.isArray( raw ) ? raw.map( parseLevel ) : [];

  return levels.length > 0 && levels.every( level => level !== null )
    ? levels as ConveyorLevel[]
    : DEFAULT_CONFIG.levels;
}

function waveSize( value: unknown, fallback: number ): number
{
  const number = typeof value === "number" ? value : Number( value );
  return Number.isInteger( number ) && number >= 1 && number <= MAX_WAVE ? number : fallback;
}

function breathSeconds( value: unknown, fallback: number ): number
{
  const number = typeof value === "number" ? value : Number( value );
  return Number.isFinite( number ) && number >= 0 && number <= MAX_BREATH_SEC ? number : fallback;
}

function structureSize( value: unknown, fallback: number ): number
{
  const number = typeof value === "number" ? value : Number( value );
  return Number.isInteger( number ) && number >= 1 && number <= MAX_STRUCTURE_SIZE ? number : fallback;
}

function chance( value: unknown, fallback: number ): number
{
  const number = typeof value === "number" ? value : Number( value );
  return Number.isFinite( number ) && number >= 0 && number <= 1 ? number : fallback;
}

function normalize( raw: unknown ): ConveyorConfig
{
  const value = raw && typeof raw === "object" && !Array.isArray( raw )
    ? raw as Record<string, unknown>
    : {};
  const start_speed = positiveNumber( value.start_speed, DEFAULT_CONFIG.start_speed, MAX_SPEED );
  const max_speed = positiveNumber( value.max_speed, DEFAULT_CONFIG.max_speed, MAX_SPEED );
  const wave_min = waveSize( value.wave_min, DEFAULT_CONFIG.wave_min );
  const wave_max = waveSize( value.wave_max, DEFAULT_CONFIG.wave_max );

  return {
    levels: parseLevels( value.levels ),
    start_speed,
    max_speed: Math.max( start_speed, max_speed ),
    ramp_sec: positiveNumber( value.ramp_sec, DEFAULT_CONFIG.ramp_sec, MAX_SECONDS ),
    fill_sec: positiveNumber( value.fill_sec, DEFAULT_CONFIG.fill_sec, MAX_FILL_SEC ),
    wave_min,
    wave_max: Math.max( wave_min, wave_max ),
    breath_sec: breathSeconds( value.breath_sec, DEFAULT_CONFIG.breath_sec ),
    block_gap_sec: breathSeconds( value.block_gap_sec, DEFAULT_CONFIG.block_gap_sec ),
    freeze_sec: positiveNumber( value.freeze_sec, DEFAULT_CONFIG.freeze_sec, MAX_BREATH_SEC ),
    structure_max_depth: structureSize( value.structure_max_depth, DEFAULT_CONFIG.structure_max_depth ),
    structure_max_cols: structureSize( value.structure_max_cols, DEFAULT_CONFIG.structure_max_cols ),
    structure_max_rows: structureSize( value.structure_max_rows, DEFAULT_CONFIG.structure_max_rows ),
    light_max_depth: structureSize( value.light_max_depth, DEFAULT_CONFIG.light_max_depth ),
    light_max_cols: structureSize( value.light_max_cols, DEFAULT_CONFIG.light_max_cols ),
    light_max_rows: structureSize( value.light_max_rows, DEFAULT_CONFIG.light_max_rows ),
    heavy_first_chance: chance( value.heavy_first_chance, DEFAULT_CONFIG.heavy_first_chance ),
    heavy_after_light_chance: chance( value.heavy_after_light_chance, DEFAULT_CONFIG.heavy_after_light_chance ),
    heavy_after_heavy_chance: chance( value.heavy_after_heavy_chance, DEFAULT_CONFIG.heavy_after_heavy_chance ),
  };
}

export class ConveyorConfigService
{
  static hasCachedConfig(): boolean
  {
    return PersistentCache.read<unknown>( CONVEYOR_CONFIG_CACHE_KEY ) !== null;
  }

  private static config = normalize( PersistentCache.read<unknown>( CONVEYOR_CONFIG_CACHE_KEY ) );
  private static refreshing: Promise<ConveyorConfig> | null = null;

  public static get(): ConveyorConfig
  {
    return this.config;
  }

  public static levelAt( level: number ): ConveyorLevel
  {
    const levels = this.config.levels;
    const index = Math.min( Math.max( 1, Math.floor( level ) ), levels.length ) - 1;

    return levels[index];
  }

  public static async refresh(): Promise<ConveyorConfig>
  {
    if ( this.refreshing ) return this.refreshing;

    this.refreshing = getContentConfig<unknown>( "conveyor" )
      .then( config =>
      {
        PersistentCache.write( CONVEYOR_CONFIG_CACHE_KEY, config );
        this.config = normalize( config );
        return this.config;
      } )
      .catch( error =>
      {
        if ( isConfigMissing( error ) )
        {
          PersistentCache.remove( CONVEYOR_CONFIG_CACHE_KEY );
          this.config = normalize( null );
          return this.config;
        }
        console.warn( "[ConveyorConfig] failed to fetch config, keeping current", error );
        return this.config;
      } )
      .finally( () => { this.refreshing = null; } );
    return this.refreshing;
  }
}
